/**
 * Team settings page component for lite home navigation
 */

import { Stack, Accordion, Text, List } from "@mantine/core";
import SettingsHeader from "@/pages/Settings/SettingsHeader/SettingsHeader";
import { TeamManager } from "@/components/Team/TeamManager/TeamManager";

interface TeamSettingsPageProps {
  onExit?: () => void;
}

const TeamSettingsPage: React.FC<TeamSettingsPageProps> = () => {
  const teamGuideContent = (
    <Accordion
      variant="default"
      chevronPosition="left"
      styles={{
        item: {
          border: "none",
        },
      }}
    >
      <Accordion.Item value="guide">
        <Accordion.Control>
          <Text size="sm" fw={500}>
            Understanding Team Management & Return Allocation
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="lg">
            {/* Team Roles Section */}
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Team Roles & Permissions
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Owner:
                    </Text>{" "}
                    Full control over the account. Can add team members and team
                    leads, access all clients, manage all business settings, and
                    allocate returns to team members.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Team Lead:
                    </Text>{" "}
                    Can view all clients (even unassigned), assign clients to
                    team members, and invite new team members. Managed by return
                    allocation policy set by the owner.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Team Member:
                    </Text>{" "}
                    Can only view clients assigned to them. Managed by return
                    allocation policy set by the owner.
                  </Text>
                </List.Item>
              </List>
            </Stack>

            {/* Return Allocation Section */}
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Return Allocation Policies
              </Text>
              <Text size="sm" c="dimmed">
                Returns determine how many tax returns a team member can
                process. There are two access types:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Business Use:
                    </Text>{" "}
                    Team members can use their allocated returns for any client
                    in the business, providing maximum flexibility for teams
                    with shared workloads.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    <Text span fw={600}>
                      Limited Use:
                    </Text>{" "}
                    Team members can only use their allocated returns for
                    clients specifically assigned to them, ideal for managing
                    individual workloads and tracking utilization.
                  </Text>
                </List.Item>
              </List>
            </Stack>

            {/* Metered Billing Section */}
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                How Returns Work with Metered Billing
              </Text>
              <Text size="sm" c="dimmed">
                With metered billing, you pay based on usage:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>
                  <Text size="sm">
                    Returns are deducted for each client and tax year
                    combination activated. For example, activating both 2023 and
                    2024 tax years for one client uses 2 returns.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    Each tax return processed consumes one return credit from
                    the team member's allocated balance.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    Owners can set individual return allocations for each team
                    member to control costs and manage capacity.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    Team members can request additional returns when they run
                    low, which owners can approve or deny.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text size="sm">
                    The business maintains a central return balance that owners
                    use to allocate to team members as needed.
                  </Text>
                </List.Item>
              </List>
            </Stack>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );

  return (
    <Stack gap={8}>
      <SettingsHeader title="Team" description={teamGuideContent} />
      <TeamManager />
    </Stack>
  );
};

export default TeamSettingsPage;
